import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { getEquips, getEquipsList } from '../../api/equipments';
import { DateRangePicker } from 'react-date-range';
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { FaSearch, FaMicrophone, FaMapMarkerAlt, FaTractor, FaRupeeSign, FaFilter, FaStar, FaCalendarAlt } from 'react-icons/fa';
import { MdAgriculture, MdVerified } from 'react-icons/md';
import { BsArrowRight } from 'react-icons/bs';
import Dropdown from '../../components/dropdown/Dropdown';
import Cookies from 'js-cookie';

const Dashboard = () => {
  // Tab and search/filter state
  const [activeTab, setActiveTab] = useState('equipment'); // options: equipment, land, products
  const [searchInput, setSearchInput] = useState('');
  const [change, setChange] = useState(false);
  const [perDay, setPerDay] = useState(50000);
  const [visible1, setVisible1] = useState(false);

  // Data for equipments and land listings
  const [equipments, setEquipments] = useState([]);
  const [equipList, setEquipList] = useState([]);

  // Products state: listing and new product form
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    price: '',
    image: ''
  });
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [productError, setProductError] = useState('');

  // Voice Recognition Setup
  const { transcript } = useSpeechRecognition();

  // Date Range Selection state
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  });
  const selectionRange = {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    key: 'selection'
  };

  // Sample land listings data (replace with API data as needed)
  const landListings = [
    {
      id: 1,
      title: "Fertile Farmland",
      location: "Karnataka",
      area: "25 Acres",
      daily_rental: 45000,
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef"
    },
    {
      id: 2,
      title: "Paddy Fields",
      location: "Punjab",
      area: "30 Acres",
      daily_rental: 38000,
      image: "https://images.unsplash.com/photo-1589328956162-4e2f54b36881"
    },
    {
      id: 3,
      title: "Organic Farm Plot",
      location: "Maharashtra",
      area: "20 Acres",
      daily_rental: 42000,
      image: "https://images.unsplash.com/photo-1464226184884-fa280b87c399"
    }
  ];

  // Fallback sample equipments
  const sampleEquipments = [
    {
      id: 1,
      title: "Mahindra 575 DI Tractor",
      location: "Ludhiana, Punjab",
      daily_rental: 2500,
      image: "https://images.unsplash.com/photo-1605338198618-d6c99f5a5e96?q=80",
      specifications: { model: "575 DI XP Plus", horsepower: "47 HP", condition: "Excellent" },
      owner: "Singh Agro Services",
      rating: 4.8,
      reviews: 156,
      available: true,
      featured: true
    },
    {
      id: 2,
      title: "Swaraj 744 FE Tractor",
      location: "Karnal, Haryana",
      daily_rental: 2200,
      image: "https://images.unsplash.com/photo-1589328956162-4e2f54b36881?q=80",
      specifications: { model: "744 FE", horsepower: "44 HP", condition: "Good" },
      owner: "Haryana Tractors",
      rating: 4.6,
      reviews: 98,
      available: true,
      featured: false
    },
    // Add more sample equipments as needed
  ];

  // Equipment Card Component
  const EquipmentCard = ({ equipment }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <div className="relative">
        <img 
          src={equipment.image} 
          alt={equipment.title}
          className="w-full h-56 object-cover transform group-hover:scale-105 transition-transform duration-300"
        />
        {equipment.featured && (
          <div className="absolute top-4 left-4">
            <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-medium flex items-center">
              <FaStar className="mr-1" /> Featured
            </span>
          </div>
        )}
        {equipment.available && (
          <span className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
            Available Now
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{equipment.title}</h3>
            <div className="flex items-center text-gray-600">
              <FaMapMarkerAlt className="mr-2 text-green-600" />
              <span>{equipment.location}</span>
            </div>
          </div>
          <div className="flex items-center bg-green-50 px-3 py-1 rounded-lg">
            <FaStar className="text-yellow-400 mr-1" />
            <span className="font-semibold text-green-700">{equipment.rating}</span>
            <span className="text-gray-500 text-sm ml-1">({equipment.reviews})</span>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Specifications</h4>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(equipment.specifications).map(([key, value]) => (
              <div key={key} className="flex items-center text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                <span className="text-gray-600 capitalize">{key}:</span>
                <span className="ml-1 font-medium text-gray-900">{value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
              <MdVerified className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-900 font-medium">{equipment.owner}</p>
              <p className="text-xs text-gray-500">Verified Seller</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <div className="text-green-600 font-bold text-xl">
              ₹{equipment.daily_rental.toLocaleString()}/day
            </div>
            <div className="text-gray-500 text-sm flex items-center">
              <FaCalendarAlt className="mr-1" /> Immediate Booking
            </div>
          </div>
          <button className="bg-green-600 text-white px-6 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center group">
            Book Now
            <BsArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );

  // Product Card Component
  const ProductCard = ({ product }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group p-4">
      <img 
        src={product.image} 
        alt={product.title}
        className="w-full h-48 object-cover mb-4 rounded"
      />
      <h3 className="text-xl font-bold text-gray-900 mb-2">{product.title}</h3>
      <p className="text-gray-600 mb-2">{product.description}</p>
      <div className="text-green-600 font-bold text-xl mb-4">
        ₹{parseInt(product.price).toLocaleString()}
      </div>
      <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition">
        View Details
      </button>
    </div>
  );

  // Update search input based on voice recognition transcript
  useEffect(() => {
    if (transcript) {
      setSearchInput(transcript);
    }
  }, [transcript]);

  // Fetch equipments and equipment list data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const equipmentsData = await getEquips();
        const equipListData = await getEquipsList();
        setEquipments(equipmentsData.length ? equipmentsData : sampleEquipments);
        setEquipList(equipListData);
      } catch (error) {
        console.error("Error fetching equipment data:", error);
        setEquipments(sampleEquipments);
      }
    };
    fetchData();
  }, [change]);

  // Fetch products using GET endpoint when Products tab is active
  useEffect(() => {
    if (activeTab === 'products') {
      const fetchProducts = async () => {
        try {
          const token = Cookies.get('access-token');
          const res = await fetch('https://krishi-mart-backend.onrender.com/api/products', {
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await res.json();
          if (res.ok) {
            setProducts(data.products || []);
          } else {
            console.error(data.message);
          }
        } catch (error) {
          console.error("Error fetching products:", error);
        }
      };
      fetchProducts();
    }
  }, [activeTab]);

  // Handle new product form input changes
  const handleProductChange = (e) => {
    setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
  };

  // Submit new product via POST endpoint
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setLoadingProduct(true);
    setProductError('');
    try {
      const token = Cookies.get('access-token');
      const res = await fetch('https://krishi-mart-backend.onrender.com/api/products', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newProduct)
      });
      const data = await res.json();
      if (res.ok) {
        // Append new product to product list and clear the form
        setProducts(prev => [...prev, data.product]);
        setNewProduct({ title: '', description: '', price: '', image: '' });
      } else {
        setProductError(data.message || "Failed to add product");
      }
    } catch (error) {
      console.error("Error adding product:", error);
      setProductError("An error occurred while adding the product.");
    } finally {
      setLoadingProduct(false);
    }
  };

  // Handle date range selection from the DateRangePicker
  const handleSelect = (ranges) => {
    setDateRange(ranges.selection);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 via-green-500 to-green-600 py-8">
        <div className="max-w-full px-4 sm:px-6 lg:px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-6">
              Krishi Mart Marketplace
            </h1>
            <p className="text-xl text-green-50 max-w-2xl mx-auto">
              India's largest platform for agricultural equipment rentals, land leasing and more.
            </p>
          </div>
          {/* Enhanced Search Bar */}
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center bg-white rounded-xl shadow-lg p-2">
              <div className="flex-1 flex items-center">
                <FaSearch className="text-gray-400 ml-4 text-xl" />
                <input
                  type="search"
                  className="w-full px-4 py-3 outline-none text-lg"
                  placeholder="Search for equipment, land or products..."
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setChange(!change) }}
                />
              </div>
              <button 
                onClick={SpeechRecognition.startListening}
                className="p-3 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                title="Voice Search"
              >
                <FaMicrophone className="w-5 h-5" />
              </button>
              <button className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 transition-colors ml-2 font-medium">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="flex justify-center mb-12 mt-8">
        <div className="bg-white rounded-xl shadow-sm p-2 inline-flex">
          <button
            className={`px-8 py-3 rounded-lg transition-all duration-300 flex items-center ${activeTab === 'equipment' ? 'bg-green-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('equipment')}
          >
            <FaTractor className={`mr-2 ${activeTab === 'equipment' ? 'text-white' : 'text-green-600'}`} />
            Equipment Rental
          </button>
          <button
            className={`px-8 py-3 rounded-lg transition-all duration-300 flex items-center ml-2 ${activeTab === 'land' ? 'bg-green-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('land')}
          >
            <MdAgriculture className={`mr-2 ${activeTab === 'land' ? 'text-white' : 'text-green-600'}`} />
            Land Leasing
          </button>
          <button
            className={`px-8 py-3 rounded-lg transition-all duration-300 flex items-center ml-2 ${activeTab === 'products' ? 'bg-green-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('products')}
          >
            <FaFilter className={`mr-2 ${activeTab === 'products' ? 'text-white' : 'text-green-600'}`} />
            Products
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {activeTab === 'equipment' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipments
              ?.filter(equipment => 
                equipment?.title?.toLowerCase().includes(searchInput.toLowerCase()) &&
                equipment?.daily_rental <= perDay
              )
              .map(equipment => (
                <EquipmentCard key={equipment.id} equipment={equipment} />
              ))
            }
          </div>
        )}

        {activeTab === 'land' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {landListings
              .filter(land => 
                land?.title?.toLowerCase().includes(searchInput.toLowerCase()) &&
                land?.daily_rental <= perDay
              )
              .map(land => (
                <div key={land.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <img 
                    src={land.image} 
                    alt={land.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900">{land.title}</h3>
                    <div className="flex items-center text-gray-600 mt-2">
                      <FaMapMarkerAlt className="mr-1" />
                      <span>{land.location}</span>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-green-600 font-semibold">
                        <FaRupeeSign className="inline" />
                        {land.daily_rental}/acre
                      </div>
                      <button className="px-4 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200">
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            {/* New Product Form */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Add New Product</h2>
              {productError && <p className="text-red-500 mb-4">{productError}</p>}
              <form onSubmit={handleProductSubmit} className="grid grid-cols-1 gap-4">
                <input 
                  type="text"
                  name="title"
                  value={newProduct.title}
                  onChange={handleProductChange}
                  placeholder="Product Title"
                  required
                  className="border p-2 rounded"
                />
                <textarea 
                  name="description"
                  value={newProduct.description}
                  onChange={handleProductChange}
                  placeholder="Product Description"
                  required
                  className="border p-2 rounded"
                />
                <input 
                  type="number"
                  name="price"
                  value={newProduct.price}
                  onChange={handleProductChange}
                  placeholder="Price"
                  required
                  className="border p-2 rounded"
                />
                <input 
                  type="text"
                  name="image"
                  value={newProduct.image}
                  onChange={handleProductChange}
                  placeholder="Image URL"
                  required
                  className="border p-2 rounded"
                />
                <button 
                  type="submit"
                  disabled={loadingProduct}
                  className="bg-green-600 text-white p-3 rounded hover:bg-green-700 transition-colors"
                >
                  {loadingProduct ? 'Adding...' : 'Add Product'}
                </button>
              </form>
            </div>
            {/* Product List with History Product Heading */}
            <h2 className="text-2xl font-bold mb-6 text-gray-900">History Product</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.length > 0 ? (
                products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))
              ) : (
                <p className="text-center col-span-full text-gray-600">No products found.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
